import { ComponentType, useMemo } from "react";

import { BulkActionsDataContext } from "components/BulkActions/Context";
import { BulkActionsDrawerResultsStepChildrenProps } from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepBody, {
  type GetTabLabel,
} from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import { BulkActionResultsStepTabItems } from "components/BulkActions/Drawer/ResultsStep/helpers";
import {
  BulkActionResultsMetadata,
  BulkActionResultsMetadataMap,
} from "components/BulkActions/helpers";
import { BulkActionResultTabs } from "components/BulkActions/types";
import useTypedContext from "hooks/useTypedContext";
import { FetchStackRunTaskData } from "shared/Stack/useTaskCreate/gql";
import { FetchStackTriggerData } from "shared/Stack/useTriggerStack/gql";
import { Stack } from "types/generated";

import { StackBulkActions } from "../types";
import { type RunToPoll } from "./gql";
import StacksBulkActionsResultsItem, { StacksBulkActionsResultsPollingItemProps } from "./Item";
import StacksBulkActionsResultsItemRunTask from "./ItemRunTask";
import StacksBulkActionsResultsItemTrigger from "./ItemTrigger";
import useStacksBulkActionsResultsRunStatesPolling from "./useBulkRunStatesPolling";

type StacksBulkActionsResultsProps = {
  resultsTabItems: BulkActionResultsStepTabItems<Stack>;
  bulkActionResultsMetadata: BulkActionResultsMetadataMap;
  stopExecutionForItem: (id: string) => void;
  actionKey?: StackBulkActions;
} & BulkActionsDrawerResultsStepChildrenProps;

const RESULT_ITEM_COMPONENTS: Partial<
  Record<StackBulkActions, ComponentType<StacksBulkActionsResultsPollingItemProps>>
> = {
  [StackBulkActions.Trigger]: StacksBulkActionsResultsItemTrigger,
  [StackBulkActions.RunTask]: StacksBulkActionsResultsItemRunTask,
};

const getRunIdFromMetadata = (metadata?: BulkActionResultsMetadata): string | undefined => {
  const data = metadata?.data as FetchStackTriggerData | FetchStackRunTaskData | undefined;
  return (
    (data as FetchStackTriggerData)?.runTrigger?.id ||
    (data as FetchStackRunTaskData)?.taskCreate?.id
  );
};

const StacksBulkActionsResults = ({
  resultsTabItems,
  bulkActionResultsMetadata,
  stopExecutionForItem,
  actionKey,
  ...restProps
}: StacksBulkActionsResultsProps) => {
  const { isExecutingBulkActions } = useTypedContext(BulkActionsDataContext);

  const ResultItemComponent: ComponentType<StacksBulkActionsResultsPollingItemProps> = actionKey
    ? (RESULT_ITEM_COMPONENTS[actionKey] ?? StacksBulkActionsResultsItem)
    : StacksBulkActionsResultsItem;

  const getTabLabel = useMemo<GetTabLabel | undefined>(() => {
    if (actionKey === StackBulkActions.Trigger || actionKey === StackBulkActions.RunTask) {
      return { [BulkActionResultTabs.Completed]: "Triggered" };
    }

    return undefined;
  }, [actionKey]);

  const shouldPollRunStates =
    (actionKey === StackBulkActions.Trigger || actionKey === StackBulkActions.RunTask) &&
    !isExecutingBulkActions;

  const runsToPoll = useMemo<RunToPoll[]>(() => {
    if (!shouldPollRunStates) return [];

    const runs: RunToPoll[] = [];

    resultsTabItems[BulkActionResultTabs.Completed].entitiesList.forEach(({ items }) => {
      items.forEach((stack) => {
        const runId = getRunIdFromMetadata(bulkActionResultsMetadata.get(stack.id));
        if (runId) {
          runs.push({ stackId: stack.id, runId });
        }
      });
    });

    return runs;
  }, [shouldPollRunStates, resultsTabItems, bulkActionResultsMetadata]);

  const { runStates } = useStacksBulkActionsResultsRunStatesPolling(
    runsToPoll,
    shouldPollRunStates
  );

  return (
    <BulkActionsDrawerResultsStepBody
      {...restProps}
      counter={resultsTabItems}
      getTabLabel={getTabLabel}
    >
      {({ currentTab }) =>
        resultsTabItems[currentTab].count === 0 ? (
          <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
        ) : (
          resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
            items.map((stack) => {
              const metadata = bulkActionResultsMetadata.get(stack.id);
              const runId = getRunIdFromMetadata(metadata);
              const polledState = runId ? runStates?.get(runId) : undefined;

              return (
                <ResultItemComponent
                  key={stack.id}
                  tab={currentTab}
                  stack={stack}
                  resultsMetadata={metadata}
                  stopExecution={() => stopExecutionForItem(stack.id)}
                  status={status}
                  polledState={polledState}
                />
              );
            })
          )
        )
      }
    </BulkActionsDrawerResultsStepBody>
  );
};

export default StacksBulkActionsResults;
